import { useTranslation } from "react-i18next";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import {
  exportInventoryMovements,
  getInventoryMovements,
} from "../../../../services/productApiFn";
import { useDispatch, useSelector } from "react-redux";
import {
  setInventoryCurrentPage,
  setInventoryPageLimit,
  setInventoryTotalCount,
  setInventoryTotalPages,
} from "../../../../lib/slices/pagination/inventoryPageSlice";
import useDebounce from "../../../../hooks/useDebounce";
import { useEffect, useMemo, useRef, useState } from "react";
import { handleOpenSnackBar } from "../../../../utils";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import { PricingDialog } from "./PicingDialog";
import { InventoryFilters } from "./Filters";
import { Stack } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import GlobalButton from "../../../../components/CustomButton";
import {
  setInventoryMovementEndDate,
  setInventoryMovementName,
  setInventoryMovementStartDate,
  setInventoryMovementType,
  setInventoryMovementVendorId,
} from "../../../../lib/slices/searchKeysSlice";
import { APP_ICONS } from "../../../../assets";
import { CSVLink } from "react-csv";
import moment from "moment";
import {
  CURRENCY,
  INVENTORY_MOVEMENT_REASON,
} from "../../../../constants/Variables";

const headerData = [
  "createdAt",
  "store",
  "productDetails",
  "reason",
  "quantityChange",
  "previousQuantity",
  "currentQuantity",
  "pricingDetails",
];

const tableRows = [
  "created_date",
  "inventory_store",
  "inventory_product_detail",
  "inventory_movement_reason",
  "inventory_quantity_change",
  "inventory_previous_quantity",
  "inventory_current_quantity",
  "inventory_pricing_details",
];

export function Inventory() {
  // dispatch actions
  const dispatch = useDispatch();

  // Open pricing dialog ref
  const pricingDialogRef = useRef(null);
  const csvLinkRef = useRef(null);

  // Tracking state
  const { t } = useTranslation(["products", "common"]);

  // loading state
  const [isLoading, setIsLoading] = useState({
    allInventory: true,
  });

  // inventory state
  const [allInventory, setAllInventory] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [shouldDownloadCsv, setShouldDownloadCsv] = useState(false);

  // get user data
  const userData = useSelector((state) => state.userData.user);

  // get current language
  const { currentLanguage } = useSelector((state) => state.languageReducer);

  // get pagination data
  const pagination = useSelector((state) => state.inventoryPaginationReducer);

  // get search keys
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);

  // get debounced search term
  const debouncedSearchTerm = useDebounce(
    searchKeys.inventoryMovementName,
    1000,
    true,
    () => {
      dispatch(setInventoryCurrentPage(1));
    },
  );

  // fetch inventory movements
  const fetchInventoryMovements = async (
    search,
    movementType,
    page,
    limit,
    startDate,
    endDate,
    vendorId,
  ) => {
    try {
      const params = {
        search: search,
        movementType: movementType,
        page: page,
        limit: limit,
        startDate: startDate,
        endDate: endDate,
        supplierId: vendorId === "all" ? "" : vendorId,
      };
      setIsLoading((prev) => ({ ...prev, allInventory: true }));
      const response = await getInventoryMovements(params);
      if (response.data.status === "success") {
        const pagination = response?.data.data.pagination;
        let data = response?.data.data.movements;
        setAllInventory(data);
        dispatch(setInventoryPageLimit(pagination.limit));
        dispatch(setInventoryCurrentPage(pagination.page));
        dispatch(setInventoryTotalCount(pagination.total));
        dispatch(setInventoryTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, allInventory: false }));
    }
  };

  useEffect(() => {
    if (userData) {
      fetchInventoryMovements(
        debouncedSearchTerm,
        searchKeys.inventoryMovementType,
        pagination.page,
        pagination.limit,
        searchKeys.inventoryMovementStartDate,
        searchKeys.inventoryMovementEndDate,
        searchKeys.inventoryMovementVendorId,
      );
    }
  }, [
    userData,
    debouncedSearchTerm,
    searchKeys.inventoryMovementType,
    searchKeys.inventoryMovementVendorId,
    pagination.page,
    pagination.limit,
  ]);

  // handle page change
  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setInventoryCurrentPage(page));
  };

  // handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setInventoryPageLimit(limit));
    dispatch(setInventoryCurrentPage(1));
  };

  // handle view pricing details
  const handleViewPricingDetails = (row) => {
    pricingDialogRef.current.openDialog({ data: row });
  };

  // handle reset filters
  const handleResetFilters = () => {
    dispatch(setInventoryMovementName(""));
    dispatch(setInventoryMovementType("All"));
    dispatch(setInventoryMovementVendorId("all"));
    dispatch(setInventoryMovementStartDate(null));
    dispatch(setInventoryMovementEndDate(null));
    dispatch(setInventoryCurrentPage(1));
    fetchInventoryMovements("", "All", 1, pagination.limit, null, null, "all");
  };

  const prepareExportData = async () => {
    try {
      const response = await exportInventoryMovements({
        supplierId:
          searchKeys.inventoryMovementVendorId === "all"
            ? ""
            : searchKeys.inventoryMovementVendorId,
      });

      if (response.data.status === "success") {
        let data = response?.data.data;
        return data.map((item) => ({
          "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
          "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
          "Product Name": item.productId?.name[currentLanguage] || "-",
          "Product SKU": item.sku || "-",
          Reason:
            INVENTORY_MOVEMENT_REASON.find((x) => x.value === item.reason)
              ?.label[currentLanguage] || "-",
          "Quantity Change": item.quantityChange,
          "Previous Quantity": item.previousQuantity,
          "Current Quantity": item.currentQuantity,
          "Purchase Price Without VAT": `${item.pricing?.price} ${CURRENCY}`,
          "Profit Margin": `${item.pricing?.profitMargin}%`,
          Discount: `${item.pricing?.discount} ${CURRENCY}`,
          "Final Price": `${item.pricing?.finalPrice} ${CURRENCY}`,
        }));
      } else {
        handleOpenSnackBar("error", response.data.message);
        return [];
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
      return [];
    }
    return [];
  };

  useEffect(() => {
    if (shouldDownloadCsv && exportData.length > 0) {
      csvLinkRef.current?.link?.click();
      setShouldDownloadCsv(false);
    }
  }, [shouldDownloadCsv, exportData]);

  const handleExportCsv = async () => {
    const data = await prepareExportData();
    if (data.length === 0) return;
    setExportData(data);
    setShouldDownloadCsv(true);
  };

  // render header actions
  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"export_button"}
          btnText={""}
          startIcon={<RefreshIcon fontSize="small" />}
          handleClickBtn={handleResetFilters}
          styleProps={{
            width: 40,
            minWidth: 40,
            height: 40,
            p: 0,
            "& .MuiButton-startIcon": {
              margin: 0,
            },
          }}
        />
        <CSVLink
          ref={csvLinkRef}
          data={exportData}
          filename={"inventory-movements.csv"}
          className="csv-link"
          style={{ display: "none" }}
        />
        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
          handleClickBtn={handleExportCsv}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />
      </Stack>
    );
  };

  // render table
  const renderTable = useMemo(() => {
    return (
      <DynamicPaginatedTable
        minWidth={1200}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allInventory || []}
        isLoading={isLoading.allInventory}
        showDynamicPagination={true}
        paginatedData={pagination}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        handleViewPricingDetails={handleViewPricingDetails}
      />
    );
  }, [allInventory, isLoading.allInventory]);

  return (
    <PaperContainer
      title={t("inventoryMovements")}
      subtitle={t("viewInventoryMovements")}
      headerActions={renderHeaderAction()}
      searchActions={<InventoryFilters fetchData={fetchInventoryMovements} />}
    >
      {renderTable}

      <PricingDialog ref={pricingDialogRef} />
    </PaperContainer>
  );
}
